<script lang="ts" setup>
import HeartCrackIcon from "@/components/icon/HeartCrackIcon.vue";
import AudioPlay, {
  AudioPlayInstance,
} from "@/components/mobile/AudioPlay.vue";
import {
  DEPICT_MATCH_REG,
  SYMBOL_MATCH_REG,
} from "@/components/mobile/chat/chatUtils";
import ChatMsgUnlock from "@/components/mobile/chat/common/ChatMsgUnlock.vue";
import TranslateTrigger from "@/components/mobile/translate/TranslateTrigger.vue";
import UserAvatar from "@/components/UserAvatar.vue";
import { useMarkdownHTML } from "@/composable/text/useMarkdown";
import { DEFAULT_COST } from "@/enums/chatEnums";
import { timeAgoPrecisely } from "@/modules/time";
import { IChatMessageItem } from "@/types/chat";
import { ChatMessageItemInstance } from "@/types/component";
import { useRawImageUrl } from "@/use/common";
import { showImagePreview } from "vant";
import { computed, ref } from "vue";
import { useRouter } from "vue-router";

const props = withDefaults(defineProps<IChatMessageItem>(), {
  mateUuid: "",
  position: "start",
  avatarUrl: "",
  msg: "",
  name: "",
  time: "",
  reactions: () => [],
  enableReactive: false,
  enableAvatarClick: false,
  mateToneId: -1,
  enableAudio: false,
  isLocked: false,
  requireUnlock: 0,
  unlockCost: DEFAULT_COST,
  currentMateToneId: -1,
  enableAvatarUrl: true,
  enableTranslate: false,
  size: "base",
});

const emit = defineEmits<{
  (e: "onLongPress"): void;
  (e: "onPlayAudio", ref: HTMLAudioElement): void;
  (e: "onEdit"): void;
}>();

const router = useRouter();
const bubbleRef = ref<HTMLElement | null>(null);
const audioRef = ref<AudioPlayInstance | null>(null);
const translateResult = ref<string>("");
const { html: contentHtml } = useMarkdownHTML(() => props.msg, {
  escapeHTML: false,
  transformBeforeLastBreak: props.enableParseMd,
});
const { html: translateHtml } = useMarkdownHTML(translateResult, {
  escapeHTML: false,
});
const audioMsg = computed(() => {
  // 过滤掉描述性文本和包裹的符号
  return props.msg
    .replaceAll(DEPICT_MATCH_REG, "")
    .replaceAll(SYMBOL_MATCH_REG, "");
});

const onAvatarClick = () => {
  if (props.enableAvatarClick) {
    router.push({ name: "mateHome", params: { uuid: props.mateUuid } });
  }
};

defineExpose<ChatMessageItemInstance>({
  id: props.id,
  onAvatarClick,
  audioRef,
});

const clickImageMessage = () => {
  showImagePreview([useRawImageUrl(props.media!.url!)]);
};

const showAudioPlay = computed(
  () =>
    props.enableAudio &&
    !props.isLocked &&
    (props.mateToneId || props.currentMateToneId) &&
    audioMsg.value.length > 0
);
</script>
<template>
  <div
    :class="{
      'chat-start': position === 'start',
      'chat-end': position === 'end',
      'gap-0': !enableAvatarUrl,
    }"
    class="chat"
  >
    <div v-if="name || time" class="chat-header gap-1">
      <span v-if="name" class="text-base-content/50">{{ name }}</span>
      <time v-if="time" class="text-xs opacity-50">
        {{ timeAgoPrecisely(time).time }}
      </time>
    </div>
    <user-avatar
      v-if="enableAvatarUrl"
      :avatar-url="avatarUrl"
      class="chat-image h-10 self-start"
      @click="onAvatarClick"
    />
    <div
      ref="bubbleRef"
      :class="[
        'chat-bubble flex p-2 sm:px-4 max-w-full sm:max-w-[90%]',
        position === 'start' ? '/60 border bg-base-100' : ' bg-green-400/10',
        requireUnlock && 'border border-pink-500/50',
      ]"
    >
      <div class="flex flex-col">
        <slot name="msg-start" />
        <slot>
          <div class="flex flex-col gap-2">
            <div
              class="chat-container"
              :class="{ 'prose-xs': size === 'xs' }"
              v-html="
                contentHtml || media
                  ? contentHtml
                  : `[${$t('Message.newsSeemsMissing')}]`
              "
            />
            <div v-if="media" class="w-[10rem]" @click="clickImageMessage">
              <ImageCard :is-nsfw="media.isNsfw" :media="media" />
            </div>
          </div>
          <div
            v-if="translateHtml"
            class="chat-container"
            v-html="translateHtml"
          />
          <ChatMsgUnlock
            v-if="isLocked"
            :msgId="id!"
            :unlock-cost="unlockCost"
          />
        </slot>
        <slot name="msg-end" />
      </div>
    </div>
    <div class="chat-footer pt-1 flex gap-1">
      <div v-if="reactions.includes('dislike')" class="badge bg-neutral/20">
        <heart-crack-icon class="icon-xs" />
      </div>
      <div class="join bg-neutral/50 rounded-full overflow-hidden">
        <AuthLock v-if="showAudioPlay" :type="2">
          <audio-play
            v-if="showAudioPlay"
            :id="id"
            ref="audioRef"
            :mate-tone-id="mateToneId"
            :msg="audioMsg"
            :new-mate-tone-id="currentMateToneId"
            :url="audioUrl"
            class="text-base-content/50 rounded-none join-item w-fit"
            icon-type="wave"
            is-chat
            playBtnClass="border-0 btn-ghost"
            @on-play-audio="emit('onPlayAudio', $event)"
          />
        </AuthLock>
        <button
          v-if="enableReactive && !isLocked"
          class="btn btn-xs text-primary rounded-none join-item"
          @click="emit('onEdit')"
        >
          <i class="iconify mingcute--edit-line" />
        </button>
        <TranslateTrigger
          v-if="msg && enableTranslate"
          v-model:result="translateResult"
          :translate-target="msg"
          class="btn-xs text-primary rounded-none join-item"
        />
        <button
          v-if="enableReactive && !isLocked"
          class="btn btn-xs text-primary rounded-none join-item"
          @click="emit('onLongPress')"
        >
          <i class="iconify mingcute--more-1-line" />
        </button>
        <slot name="extraAction" />
      </div>
    </div>
  </div>
</template>

<style lang="postcss">
.chat {
  column-gap: 0.9rem;
}

.chat-bubble {
  display: flex;
  align-items: center;
}

.chat-start .chat-bubble {
  border-radius: 1rem !important;
  border-top-left-radius: 0 !important;
}

.chat-end .chat-bubble {
  border-radius: 1rem !important;
  border-top-right-radius: 0 !important;
  @apply border border-green-600;
}

.chat-bubble::before {
  display: none;
  top: 0;
  transform: rotateX(180deg);
}
</style>
